import {
  comprimento,
  limitar,
  multiplicar,
  normalizar,
  somar,
  subtrair,
  vetor,
  type Vetor3,
} from '../geometria/vetor3'
import {
  amostrarCinematica,
  colisao,
  duracaoRealS,
  inicioDaRolagem,
  rolagemEm,
  type QuadroCinematica,
  type TipoColisao,
} from './timelineCinematica'
import { primeiroContato, type Contato, type SuperficieInterna } from './superficiesInternas'

/**
 * Reação do ocupante à colisão, com ou sem cinto.
 *
 * `comCinto` é exatamente a timeline didática existente — nenhuma regressão
 * para quem já usava a aba. `semCinto` NÃO é um segundo conjunto de
 * quadros-chave escritos à mão: é a integral da mesma diferença de velocidade
 * que a timeline já declara (`velocidadeVeiculo`), o que faz a curva "sem
 * cinto" ser consequência do tipo de colisão escolhido, não um número mágico
 * novo. Continua sendo recurso de ensino, não física biomecânica — a mesma
 * ressalva de `timelineCinematica.ts`.
 */
export type ModoRetencao = 'comCinto' | 'semCinto'

export interface ReacaoOcupante {
  deslocamentoRelativo: Vetor3
  inclinacaoTroncoRad: number
  inclinacaoLateralTroncoRad: number
  inclinacaoCabecaRad: number
  /** o cinto está em carga neste instante */
  retido: boolean
  /** superfície e instante do primeiro contato, quando o corpo já chegou nela */
  contato: Contato | null
  /**
   * O corpo terminaria fora do envelope do habitáculo sem encontrar nada no
   * caminho — aproximação de ejeção: sem superfície alguma a deter o corpo, o
   * deslocamento livre não para de crescer.
   */
  ejetado: boolean
}

/** Acima disto, sem nenhum contato registrado, o corpo é lido como ejetado. */
export const LIMIAR_EJECAO_M = 1.0

/** Amplificação máxima da inclinação sem cinto em relação à curva retida. */
const AMPLIFICACAO_MAXIMA = 2.4

const areaTrapezio = (y0: number, y1: number, largura: number): number => ((y0 + y1) / 2) * largura

/**
 * Integral de |v(0) − v(t)| entre 0 e `ateT`.
 *
 * `v` é a velocidade do veículo já declarada pela timeline (`velocidadeVeiculo`,
 * normalizada 0..1) — linear por trecho entre os quadros-chave. O ocupante sem
 * cinto mantém a velocidade que tinha em t=0: é essa diferença, integrada, que
 * dá o deslocamento relativo dele.
 */
function integralVelocidadeRelativa(tipo: TipoColisao, ateT: number): number {
  const quadros = colisao(tipo).quadros
  const v0 = quadros[0]!.velocidadeVeiculo
  let soma = 0

  for (let i = 0; i < quadros.length - 1; i += 1) {
    const a = quadros[i]!
    const b = quadros[i + 1]!
    if (a.tempo >= ateT) break
    const fim = Math.min(b.tempo, ateT)
    const larguraSegmento = b.tempo - a.tempo
    const fracao = larguraSegmento <= 0 ? 0 : (fim - a.tempo) / larguraSegmento
    const vFim = a.velocidadeVeiculo + (b.velocidadeVeiculo - a.velocidadeVeiculo) * fracao
    soma += areaTrapezio(Math.abs(v0 - a.velocidadeVeiculo), Math.abs(v0 - vFim), fim - a.tempo)
    if (fim >= ateT) break
  }

  return soma
}

/**
 * Deslocamento livre por diferença de velocidade — a conta que vale para todo
 * tipo SEM rolagem, e também para a fase anterior ao início da rolagem num
 * tipo que rola (a "lateral que capota" tem impacto antes de perder o apoio).
 *
 * Antes de `tempoRetencao` ninguém foi retido ainda — com ou sem cinto o corpo
 * andou igual, e a curva usa exatamente os mesmos quadros da timeline. Depois
 * disso, o corpo com cinto é freado pela retenção; o sem cinto continua na
 * mesma direção (calibrada para coincidir em `tempoRetencao`), crescendo pela
 * integral da diferença de velocidade.
 */
function deslocamentoPorImpacto(tipo: TipoColisao, tempo: number): Vetor3 {
  const def = colisao(tipo)
  const referencia = amostrarCinematica(tipo, def.tempoRetencao).deslocamentoRelativoOcupante
  const magnitudeReferencia = comprimento(referencia)

  if (tempo <= def.tempoRetencao || magnitudeReferencia < 1e-6) {
    return amostrarCinematica(tipo, tempo).deslocamentoRelativoOcupante
  }

  const direcao = normalizar(referencia)
  const integralReferencia = integralVelocidadeRelativa(tipo, def.tempoRetencao) || 1e-6
  const k = magnitudeReferencia / integralReferencia
  return multiplicar(direcao, k * integralVelocidadeRelativa(tipo, tempo))
}

/**
 * Deslocamento relativo do ocupante SEM cinto, na escala 0..1 da timeline.
 *
 * Nos tipos que rolam, soma duas fases: a de impacto, por diferença de
 * velocidade (igual aos que não rolam), até `inicioDaRolagem`; e a da
 * rolagem em si, pela gravidade no referencial do veículo. No tombamento e no
 * capotamento a rolagem começa em t=0, então a fase de impacto tem duração
 * zero e isto se reduz exatamente ao que já era — a soma não é um caso novo,
 * é a generalização de que os dois tipos antigos eram o caso particular.
 */
export function deslocamentoLivre(tipo: TipoColisao, tempo: number, limiteM = LIMIAR_EJECAO_M): Vetor3 {
  const t = limitar(tempo, 0, 1)
  const def = colisao(tipo)
  if (!def.comRolagem) return deslocamentoPorImpacto(tipo, t)

  const t0 = inicioDaRolagem(tipo)
  if (t <= t0) return deslocamentoPorImpacto(tipo, t)

  const doImpacto = deslocamentoPorImpacto(tipo, t0)
  const total = somar(doImpacto, deslocamentoLivreRolando(tipo, t0, t, limiteM))
  const distancia = comprimento(total)
  return distancia > limiteM ? multiplicar(total, limiteM / distancia) : total
}

/** Aceleração da gravidade, em m/s². A mesma do solucionador. */
const GRAVIDADE = 9.81

/**
 * Gravidade vista do referencial do VEÍCULO, com ele rolado de `rolagemRad`.
 *
 * É a ideia inteira do capotamento procedural, e não precisa de calibração
 * nenhuma: com o veículo sobre as rodas ela aponta para o assento (−Y); a −90°
 * (tombado sobre o motorista), para a porta que virou piso (+X); invertido, para o TETO (+Y). Enquanto o
 * deslocamento livre usava uma única direção congelada — no capotamento,
 * `vetor(-0,04; 0; 0)`, tirada do quadro de t=0,14 —, não existia caminho até o
 * teto por construção, e o boneco sem cinto ficava no banco por mais que a
 * rolagem avançasse.
 */
export function gravidadeNoVeiculo(rolagemRad: number): Vetor3 {
  return vetor(-GRAVIDADE * Math.sin(rolagemRad), -GRAVIDADE * Math.cos(rolagemRad), 0)
}

/**
 * Deslocamento livre nos tipos que ROLAM, na janela [de, ate]: dupla integral
 * da gravidade no referencial do veículo, em tempo REAL — partindo do repouso
 * em `de`, não do início do evento.
 *
 * Não é a mesma conta da fase de impacto e não podia ser. Nas colisões o que
 * move o corpo em relação ao veículo é a variação de velocidade DELE, e a direção
 * é fixa; na rolagem é a gravidade, e a direção varre meia-volta junto com a
 * lataria. Quem detém o corpo continua sendo `primeiroContato` contra as mesmas
 * superfícies de sempre — o teto já está entre elas.
 *
 * `de` é `inicioDaRolagem(tipo)`: no tombamento e no capotamento vale 0, e a
 * janela inteira coincide com o evento, exatamente como antes desta função
 * ganhar os dois parâmetros.
 */
function deslocamentoLivreRolando(tipo: TipoColisao, de: number, ate: number, limiteM: number): Vetor3 {
  const duracao = duracaoRealS(tipo)
  const passos = 64
  let velocidade = vetor(0, 0, 0)
  let posicao = vetor(0, 0, 0)
  const janela = Math.max(ate - de, 0)
  const dt = (janela * duracao) / passos
  for (let i = 0; i < passos; i += 1) {
    const tn = de + (janela * i) / passos
    // a gravidade que o corpo sente MENOS a que o veículo já lhe transmite pelo
    // apoio: enquanto ele está sobre as rodas o banco o segura, e o corpo não cai
    const g = gravidadeNoVeiculo(rolagemEm(tipo, tn))
    const apoiado = Math.abs(rolagemEm(tipo, tn)) < 1e-6
    const a = apoiado ? vetor(0, 0, 0) : somar(g, vetor(0, GRAVIDADE, 0))
    velocidade = somar(velocidade, multiplicar(a, dt))
    posicao = somar(posicao, multiplicar(velocidade, dt))
    // dentro de uma cabine fechada a queda tem fim. Sem este limite a integral
    // livre passava de dez metros num capotamento de 1,2 s, atravessava o alcance
    // das superfícies e a leitura saía como ejeção — com o corpo bem visível
    // deitado sobre o teto
    const distancia = comprimento(posicao)
    if (distancia > limiteM) {
      posicao = multiplicar(posicao, limiteM / distancia)
      velocidade = vetor(0, 0, 0)
    }
  }
  return posicao
}

/** Amostras da trajetória livre, para detectar contra o que ela cruza primeiro. */
function amostrasLivres(
  tipo: TipoColisao,
  limiteM: number,
  passos = 24,
): Array<{ t: number; deslocamento: Vetor3 }> {
  return Array.from({ length: passos + 1 }, (_, i) => {
    const t = i / passos
    return { t, deslocamento: deslocamentoLivre(tipo, t, limiteM) }
  })
}

/**
 * Até onde o interior deixa o corpo ir, a partir da origem de contato.
 *
 * Sai das MESMAS superfícies que detêm o corpo — se uma chapa está a 60 cm, não
 * há como o corpo andar 10 m antes de encontrá-la —, em vez de um número novo.
 * Com uma folga de alcance para a travessia ser detectada e não raspada.
 */
function alcanceDoInterior(superficies: readonly SuperficieInterna[], origem: Vetor3): number {
  let maior = 0
  for (const superficie of superficies) {
    const distancia = comprimento(subtrair(superficie.ponto, origem)) + superficie.raioAlcance
    if (distancia > maior) maior = distancia
  }
  return maior > 0 ? maior : LIMIAR_EJECAO_M
}

/**
 * Reação do ocupante no instante `tempo`.
 *
 * `origem` é o ponto do corpo a partir do qual o deslocamento é medido — em
 * geral um ponto no tronco/cabeça, coerente com onde as superfícies foram
 * definidas (`superficiesDoHabitaculo`).
 */
export function reacaoDoOcupante(
  tipo: TipoColisao,
  tempo: number,
  modo: ModoRetencao,
  superficies: readonly SuperficieInterna[],
  origem: Vetor3,
): ReacaoOcupante {
  const t = limitar(tempo, 0, 1)

  if (modo === 'comCinto') {
    const quadro = amostrarCinematica(tipo, t)
    return {
      deslocamentoRelativo: quadro.deslocamentoRelativoOcupante,
      inclinacaoTroncoRad: quadro.inclinacaoTroncoRad,
      inclinacaoLateralTroncoRad: quadro.inclinacaoLateralTroncoRad,
      inclinacaoCabecaRad: quadro.inclinacaoCabecaRad,
      retido: quadro.cintoAtivo,
      contato: null,
      ejetado: false,
    }
  }

  const limite = alcanceDoInterior(superficies, origem)
  const amostras = amostrasLivres(tipo, limite)
  const contato = primeiroContato(superficies, origem, amostras)

  // trava no primeiro contato: o corpo não atravessa o que encontrou
  const tEfetivo = contato && contato.tempo < t ? contato.tempo : t
  const travado = contato != null && contato.tempo <= t

  const deslocamento = deslocamentoLivre(tipo, tEfetivo, limite)
  const quadroEfetivo = amostrarCinematica(tipo, tEfetivo)
  const magnitudeRetida = comprimento(quadroEfetivo.deslocamentoRelativoOcupante)
  const magnitudeLivre = comprimento(deslocamento)
  // sem o cinto puxando de volta, a inclinação cresce na mesma proporção em
  // que o deslocamento já cresceu além do que a retenção teria permitido
  const amplificacao =
    magnitudeRetida < 1e-6 ? 1 : Math.min(magnitudeLivre / magnitudeRetida, AMPLIFICACAO_MAXIMA)

  const semContatoAteOFim = !contato && comprimento(deslocamentoLivre(tipo, 1, limite)) > LIMIAR_EJECAO_M

  return {
    deslocamentoRelativo: deslocamento,
    inclinacaoTroncoRad: quadroEfetivo.inclinacaoTroncoRad * amplificacao,
    inclinacaoLateralTroncoRad: quadroEfetivo.inclinacaoLateralTroncoRad * amplificacao,
    inclinacaoCabecaRad: quadroEfetivo.inclinacaoCabecaRad * amplificacao,
    retido: false,
    contato: travado ? contato : null,
    ejetado: semContatoAteOFim,
  }
}

/** Só para leitura em telemetria/depuração de teste: expõe o quadro retido equivalente. */
export function quadroRetidoEmT(tipo: TipoColisao, tempo: number): QuadroCinematica {
  return amostrarCinematica(tipo, tempo)
}
